import styled, { keyframes } from 'styled-components';
// ============================================
// KEYFRAME ANIMATIONS
// ============================================
const fadeUp = keyframes `
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;
const shimmer = keyframes `
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;
const pulseGold = keyframes `
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(184, 134, 11, 0.4);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(184, 134, 11, 0);
  }
`;
// ============================================
// PAGE LAYOUT
// ============================================
export const PageWrapper = styled.div `
  min-height: 100vh;
  background: var(--bg-primary);
`;
// ============================================
// HERO SECTION
// ============================================
export const HeroSection = styled.section `
  padding: 48px 24px 32px;
  text-align: center;
  background: linear-gradient(180deg, var(--bg-secondary) 0%, var(--bg-primary) 100%);
  border-bottom: 1px solid var(--border-color);
`;
export const HeroTitle = styled.h1 `
  font-family: 'Playfair Display', 'Adamina', serif !important;
  font-size: 38px !important;
  font-weight: 600 !important;
  letter-spacing: 0.03em;
  margin-bottom: 10px !important;
  color: var(--text-primary) !important;

  span {
    color: var(--accent-gold, #B8860B);
  }

  @media (max-width: 768px) {
    font-size: 28px !important;
  }
`;
export const HeroSubtitle = styled.p `
  color: var(--text-secondary);
  font-size: 15px;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;
// ============================================
// STATS GRID (Progress Overview)
// ============================================
export const StatsGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  max-width: 900px;
  margin: -24px auto 32px;
  padding: 0 24px;
  position: relative;
  z-index: 10;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;
export const StatCard = styled.div `
  background: var(--bg-primary);
  border: 1px solid ${props => props.$highlighted ? 'var(--accent-gold)' : 'var(--border-color)'};
  padding: 24px 20px;
  text-align: center;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--accent-gold, #B8860B), var(--accent-gold-light, #D4A634));
    opacity: 0;
    transition: opacity 0.3s;
  }

  &:hover::before {
    opacity: 1;
  }

  &:hover {
    border-color: var(--accent-gold-border, rgba(184, 134, 11, 0.25));
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }

  .stat-value {
    font-family: 'Playfair Display', 'Adamina', serif;
    font-size: 32px;
    font-weight: 600;
    color: var(--accent-gold, #B8860B);
    margin-bottom: 6px;
  }

  .stat-label {
    color: var(--text-secondary);
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 1px;
    font-weight: 500;
  }

  @media (max-width: 768px) {
    padding: 20px 16px;

    .stat-value {
      font-size: 26px;
    }
  }
`;
// ============================================
// MAIN CONTENT
// ============================================
export const MainContent = styled.div `
  max-width: 1200px;
  margin: 0 auto;
  padding: 28px 24px;
`;
// ============================================
// SECTION STYLING
// ============================================
export const SectionDivider = styled.div `
  margin: 48px 0 32px;
  display: flex;
  align-items: center;
  gap: 16px;
`;
export const SectionTitle = styled.h2 `
  font-family: 'Playfair Display', 'Adamina', serif !important;
  font-size: 22px !important;
  font-weight: 600 !important;
  color: var(--text-primary) !important;
  margin: 0 !important;
  display: flex;
  align-items: center;
  gap: 12px;

  svg {
    color: var(--accent-gold, #B8860B);
    font-size: 20px;
  }
`;
export const AdminSectionTitle = styled(SectionTitle) `
  color: #dc2626 !important;

  svg {
    color: #dc2626;
  }
`;
export const SectionDescription = styled.p `
  color: var(--text-secondary);
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 28px;
`;
// ============================================
// CATEGORY STYLING
// ============================================
export const CategorySection = styled.div `
  margin-bottom: 40px;
`;
export const CategoryHeader = styled.div `
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  padding-bottom: 12px;
  border-bottom: 2px solid ${props => props.$color};

  svg {
    font-size: 20px;
    color: ${props => props.$color};
  }

  h3 {
    font-family: 'Playfair Display', 'Adamina', serif !important;
    font-size: 18px !important;
    font-weight: 600 !important;
    color: var(--text-primary) !important;
    margin: 0 !important;
  }

  p {
    margin: 0;
    margin-left: auto;
    color: var(--text-secondary);
    font-size: 13px;
  }
`;
// ============================================
// TUTORIAL CARDS GRID
// ============================================
export const TutorialsGridWrapper = styled.div `
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;
export const TutorialCard = styled.div `
  background: var(--bg-primary);
  border: 1px solid ${props => props.$completed ? '#16a34a' :
    props.$locked ? 'var(--border-color)' :
        'var(--border-color)'};
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  opacity: ${props => (props.$locked || props.$comingSoon) ? 0.7 : 1};

  ${props => props.$completed && `
    box-shadow: 0 0 20px rgba(22, 163, 74, 0.15);
  `}

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${props => props.$completed ? 'linear-gradient(90deg, #16a34a, #22c55e)' :
    'linear-gradient(90deg, var(--accent-gold, #B8860B), var(--accent-gold-light, #D4A634))'};
    opacity: ${props => props.$completed ? 1 : 0};
    transition: opacity 0.3s;
  }

  &:hover {
    border-color: ${props => props.$locked ? 'var(--border-color)' :
    props.$completed ? '#16a34a' :
        'var(--accent-gold-border, rgba(184, 134, 11, 0.25))'};
    transform: ${props => (props.$locked || props.$comingSoon) ? 'none' : 'translateY(-4px)'};
    box-shadow: ${props => (props.$locked || props.$comingSoon) ? 'none' : '0 12px 40px rgba(0, 0, 0, 0.12)'};

    &::before {
      opacity: ${props => (props.$locked || props.$comingSoon) ? 0 : 1};
    }
  }
`;
export const TutorialCardHeader = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border-color);
  background: var(--bg-secondary);
`;
export const TutorialMeta = styled.div `
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
`;
export const DifficultyBadge = styled.span `
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-radius: 2px;

  ${props => {
    switch (props.$level) {
        case 'beginner':
            return `
          background: rgba(34, 197, 94, 0.15);
          color: #16a34a;
        `;
        case 'intermediate':
            return `
          background: rgba(234, 179, 8, 0.15);
          color: #ca8a04;
        `;
        case 'advanced':
            return `
          background: rgba(239, 68, 68, 0.15);
          color: #dc2626;
        `;
        default:
            return `
          background: var(--bg-tertiary);
          color: var(--text-secondary);
        `;
    }
}}
`;
export const TimeBadge = styled.span `
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  font-size: 10px;
  font-weight: 600;
  color: var(--text-secondary);
  background: var(--bg-tertiary);
  border-radius: 2px;
`;
export const ChaptersBadge = styled.span `
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  font-size: 10px;
  font-weight: 600;
  color: var(--text-secondary);
  background: var(--bg-tertiary);
  border-radius: 2px;
`;
export const BadgeTierDisplay = styled.span `
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-radius: 2px;
  border: 1px solid;

  ${props => {
    switch (props.$tier) {
        case 'bronze':
            return `
          background: rgba(205, 127, 50, 0.15);
          color: #cd7f32;
          border-color: rgba(205, 127, 50, 0.3);
        `;
        case 'silver':
            return `
          background: rgba(192, 192, 192, 0.15);
          color: #a8a8a8;
          border-color: rgba(192, 192, 192, 0.3);
        `;
        case 'gold':
            return `
          background: var(--accent-gold-bg, rgba(184, 134, 11, 0.08));
          color: var(--accent-gold, #B8860B);
          border-color: var(--accent-gold-border, rgba(184, 134, 11, 0.25));
        `;
        case 'platinum':
            return `
          background: rgba(168, 85, 247, 0.15);
          color: #9333ea;
          border-color: rgba(168, 85, 247, 0.3);
        `;
        default:
            return `
          background: var(--bg-tertiary);
          color: var(--text-secondary);
          border-color: var(--border-color);
        `;
    }
}}
`;
export const CompletedBadgeIcon = styled.div `
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #16a34a 0%, #22c55e 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #FFFFFF;
  font-size: 18px;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    inset: -3px;
    border-radius: 50%;
    border: 2px solid rgba(22, 163, 74, 0.3);
  }
`;
export const TutorialCardBody = styled.div `
  padding: 20px;
`;
export const TutorialTitle = styled.h4 `
  font-family: 'Playfair Display', 'Adamina', serif !important;
  font-size: 17px !important;
  font-weight: 600 !important;
  color: var(--text-primary) !important;
  margin: 0 0 10px 0 !important;
`;
export const TutorialDescription = styled.p `
  color: var(--text-secondary);
  font-size: 13px;
  line-height: 1.6;
  margin: 0 0 16px 0;
`;
export const TutorialCardFooter = styled.div `
  padding: 16px 20px;
  border-top: 1px solid var(--border-color);
  background: var(--bg-secondary);
`;
export const StartButton = styled.button `
  width: 100%;
  padding: 12px 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: ${props => (props.$locked || props.$comingSoon) ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1px solid;

  ${props => props.$completed ? `
    background: var(--bg-primary);
    color: #16a34a;
    border-color: #16a34a;

    &:hover {
      background: rgba(22, 163, 74, 0.1);
    }
  ` : props.$locked || props.$comingSoon ? `
    background: var(--bg-tertiary);
    color: var(--text-tertiary);
    border-color: var(--border-color);
  ` : `
    background: var(--accent-gold, #B8860B);
    color: #FFFFFF;
    border-color: var(--accent-gold, #B8860B);

    &:hover {
      background: var(--accent-gold-dark, #8B6914);
      border-color: var(--accent-gold-dark, #8B6914);
    }
  `}
`;
export const LockedIcon = styled.span `
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--text-tertiary);
`;
export const NewBadge = styled.span `
  position: absolute;
  top: 12px;
  right: 12px;
  background: var(--accent-gold, #B8860B);
  color: #FFFFFF;
  padding: 4px 10px;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  animation: ${pulseGold} 2s ease-in-out infinite;
`;
export const FeaturedBadge = styled.span `
  position: absolute;
  top: 12px;
  left: 12px;
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  color: #FFFFFF;
  padding: 4px 10px;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 4px;
`;
// ============================================
// LOADING STATE
// ============================================
export const LoadingWrapper = styled.div `
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  text-align: center;
`;
export const LoadingSpinner = styled.div `
  width: 48px;
  height: 48px;
  border: 3px solid var(--border-color);
  border-top-color: var(--accent-gold, #B8860B);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;
export const LoadingText = styled.p `
  font-family: 'Playfair Display', 'Adamina', serif;
  font-size: 18px;
  color: var(--text-secondary);
`;
// ============================================
// EMPTY STATE
// ============================================
export const EmptyState = styled.div `
  text-align: center;
  padding: 80px 24px;
  color: var(--text-tertiary);
  background: var(--bg-secondary);
  border: 1px dashed var(--border-color);

  h3 {
    font-family: 'Playfair Display', 'Adamina', serif;
    font-size: 20px;
    color: var(--text-secondary);
    margin-bottom: 12px;
  }

  p {
    font-size: 14px;
    max-width: 400px;
    margin: 0 auto;
    line-height: 1.6;
  }
`;
